import * as React from "react"
import { ButtonBaseProps } from "@repo/design-system/types"
import { cn } from "@/lib/utils"
import { colors } from '@repo/design-system/tokens'

export interface WebButtonProps extends ButtonBaseProps {
  radius?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';
}

/**
 * Button component with styling based on design system tokens
 * 
 * This component defines button variants and sizes using classes that
 * reference our design system CSS variables defined in the tailwind config.
 * These classnames can be used directly or composed into other components.
 */
export const buttonVariants = {
  base: "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background",
  variant: {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    primary: "bg-primary text-primary-foreground hover:bg-primary/90",
    secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
    outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
    ghost: "hover:bg-accent hover:text-accent-foreground",
    link: "underline-offset-4 hover:underline text-primary",
    destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
    success: "bg-success text-white hover:bg-success/90",
    error: "bg-error text-white hover:bg-error/90",
    premium: "bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white border-0 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transform transition-transform duration-200 hover:scale-105 shadow-lg",
  },
  size: {
    xs: "h-8 px-2.5 py-0.5 text-xs",
    sm: "h-9 px-3 py-1 text-xs",
    md: "h-10 px-4 py-2 text-sm",
    lg: "h-11 px-6 py-2.5 text-base",
    xl: "h-12 px-8 py-3 text-base",
    icon: "h-10 w-10",
  },
  fullWidth: {
    true: "w-full",
  },
};

export const Button = React.forwardRef<HTMLButtonElement, WebButtonProps>(
  ({ 
    className,
    variant = "default",
    size = "md",
    radius, // Added radius prop
    disabled,
    loading,
    fullWidth,
    startIcon,
    endIcon,
    children,
    onPress,
    onClick,
    type = "button",
    asChild = false,
    ...props 
  }, ref) => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      onPress?.();
    };

    // Get the appropriate variant and size classes
    const variantClass = buttonVariants.variant[variant as keyof typeof buttonVariants.variant] || buttonVariants.variant.default;
    let sizeClass = buttonVariants.size[size as keyof typeof buttonVariants.size] || buttonVariants.size.md;
    const fullWidthClass = fullWidth ? buttonVariants.fullWidth.true : "";
    let radiusClass = "premium-radius-md"; // Default radius
    if (radius) {
      switch (radius) {
        case 'none': radiusClass = 'premium-radius-none'; break;
        case 'sm': radiusClass = 'premium-radius-sm'; break;
        case 'md': radiusClass = 'premium-radius-md'; break;
        case 'lg': radiusClass = 'premium-radius-lg'; break;
        case 'xl': radiusClass = 'premium-radius-xl'; break;
        case '2xl': radiusClass = 'premium-radius-2xl'; break;
        case '3xl': radiusClass = 'premium-radius-3xl'; break;
        case 'full': radiusClass = 'premium-radius-full'; break;
        default: radiusClass = 'premium-radius-md'; // Fallback to default
      }
    }

    return (
      <button
        className={cn(
          buttonVariants.base,
          variantClass,
          sizeClass,
          fullWidthClass,
          radiusClass, // Apply the determined radius class
          className
        )}
        ref={ref}
        disabled={disabled || loading}
        onClick={handleClick}
        type={type}
        {...props}
      >
        {loading && (
          <svg
            className="mr-2 h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        )}
        {startIcon && <span className="mr-2">{startIcon}</span>}
        {children}
        {endIcon && <span className="ml-2">{endIcon}</span>}
      </button>
    )
  }
)
Button.displayName = "Button"
